import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/ApiConfig';
import TableHeader from '../TableHeader';

function PcaConsolidationPickerModal({ show, onClose, onConfirm, showDefaultModal, consolidadoId }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);

  useEffect(() => {
    if (!show) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const cnpj = localStorage.getItem('cnpj');
        const anoBase = localStorage.getItem('anoBase');
        const res = await axios.get(`${API_BASE_URL.PCA}/all`, { params: { cnpj, anoBase }, headers: { Authorization: `Bearer ${token}` } });
        const fetched = res.data || [];

        // if there's an existing consolidation header, fetch its consolidated items to pre-select
        // build a set of keys: CODE|DFD (normalized)
        let consolidatedKeys = new Set();
        if (consolidadoId) {
          try {
            const r2 = await axios.get(`${API_BASE_URL.PCA}/consolidado/${consolidadoId}/itens`, { params: { cnpj, anoBase }, headers: { Authorization: `Bearer ${token}` } });
            const cons = r2.data || [];
            cons.forEach(ci => {
              const code = (ci.codigoItem || ci.codigo || ci['CODIGO_ITEM']);
              const lista = ci.listaDfds || ci.LISTA_DFDS || '';
              const parts = lista.split(',').map(p => p.trim()).filter(Boolean);
              if (parts.length) {
                parts.forEach(p => {
                  // extract possible DFD form (before ' - ')
                  const dfdPart = p.split('-')[0].trim();
                  const normCode = code ? String(code).toUpperCase().trim() : '';
                  const normDfd = dfdPart ? String(dfdPart).toUpperCase().replace(/\s+/g, '') : '';
                  if (normCode && normDfd) consolidatedKeys.add(`${normCode}|${normDfd}`);
                });
              } else if (ci.dfdNum) {
                const dfdPart = String(ci.dfdNum).trim();
                const normCode = code ? String(code).toUpperCase().trim() : '';
                const normDfd = dfdPart ? String(dfdPart).toUpperCase().replace(/\s+/g, '') : '';
                if (normCode && normDfd) consolidatedKeys.add(`${normCode}|${normDfd}`);
              }
            });
          } catch (e) {
            console.warn('Não foi possível obter itens consolidados existentes', e);
          }
        }

        setItems(fetched);

        // build initial selected map for items that are already consolidated (match by codigo + dfd)
        if (consolidatedKeys.size) {
          const sel = {};
          fetched.forEach(it => {
            const code = (it.codigo || it.codigoItem || it.codItem || it.item);
            const formatted = formatDfd(it.dfdNum);
            const normCode = code ? String(code).toUpperCase().trim() : '';
            const normDfd = formatted ? String(formatted).toUpperCase().replace(/\s+/g, '') : '';
            if (normCode && normDfd && consolidatedKeys.has(`${normCode}|${normDfd}`)) {
              sel[it.id] = it;
            }
          });
          setSelected(sel);
        } else {
          setSelected({});
        }
      } catch (err) {
        console.error('Erro ao carregar itens PCA', err);
        setItems([]);
        setSelected({});
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [show, consolidadoId]);

  const toggle = (id, item) => {
    setSelected(prev => ({ ...prev, [id]: prev[id] ? undefined : item }));
  };

  const handleConfirm = () => {
    const selectedItems = Object.values(selected).filter(Boolean);
    if (selectedItems.length === 0) {
      if (showDefaultModal) {
        showDefaultModal('error', 'Selecione ao menos um item para consolidar.');
      }
      return;
    }
    onConfirm(selectedItems);
  };

  const formatDfd = (dfd) => {
    if (dfd === null || dfd === undefined || dfd === '') return '';
    const s = String(dfd);
    if (s.includes('/')) return s;
    const anoBase = String(localStorage.getItem('anoBase') || new Date().getFullYear());
    const num = Number(s.replace(/\D/g, '')) || 0;
    const seq = num % 1000;
    return `${String(seq).padStart(3, '0')}/${anoBase}`;
  };

  const normalizeString = (s) => {
    if (s === null || s === undefined) return '';
    return String(s)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s\/]/g, '')
      .toUpperCase();
  };

  if (!show) return null;

  // compute filtered/display items outside of JSX to avoid complex inline IIFE parsing
  const displayItems = (() => {
    const codeCounts = {};
    items.forEach(it => {
      const code = normalizeString(it.codigo || it.codigoItem || it.codItem || it.item || '');
      if (!code) return;
      codeCounts[code] = (codeCounts[code] || 0) + 1;
    });
    const duplicateCodes = new Set(Object.keys(codeCounts).filter(k => codeCounts[k] > 1));

    return items
      .filter(it => {
        if (search) {
          const s = normalizeString(search);
          const dfdField = normalizeString(formatDfd(it.dfdNum));
          const codeField = normalizeString(it.codigo || it.codigoItem || it.codItem || '');
          const descField = normalizeString(it.descricaoItem || '');
          const reqField = normalizeString(it.secretariaDepartamento || it.requisitante || '');
          const match = dfdField.includes(s) || codeField.includes(s) || descField.includes(s) || reqField.includes(s);
          if (!match) return false;
        }
        if (duplicatesOnly) {
          const code = normalizeString(it.codigo || it.codigoItem || it.codItem || it.item || '');
          if (!code) return false;
          return duplicateCodes.has(code);
        }
        return true;
      });
  })();

  return (
    <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg" style={{ maxWidth: '95vw', height: '90vh' }}>
        <div className="modal-content h-100">
          <div className="modal-header text-white py-1" style={{ background: 'linear-gradient(90deg, #0b3383, #0e48ad)' }}>
            <h6 className="modal-title m-0 small">Selecionar registros do PCA para consolidar</h6>
            <button
              className="btn btn-link text-white p-0 mb-0"
              onClick={onClose}
              title="Fechar"
              style={{ fontSize: '0.9rem', textDecoration: 'none' }}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>
          <div className="modal-body d-flex flex-column p-0" style={{ flex: 1, overflow: 'hidden' }}>
            {loading ? (
              <div className="text-center py-1 w-100">Carregando...</div>
            ) : (
              <div className="d-flex flex-column" style={{ height: '100%' }}>
                <div className="p-2 d-flex align-items-center gap-2">
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Filtrar por DFD, descrição ou requisitante..."
                    value={search}
                    onChange={e => setSearch(e.target.value.toUpperCase())}
                    style={{ maxWidth: '420px' }}
                  />
                  <div className="form-check ms-2">
                    <input className="form-check-input" type="checkbox" value="" id="dupOnly" checked={duplicatesOnly} onChange={e => setDuplicatesOnly(e.target.checked)} />
                    <label className="form-check-label small text-muted" htmlFor="dupOnly">Apenas itens repetidos</label>
                  </div>
                </div>
                <div className="p-2 d-flex flex-column" style={{ flex: 1, overflowX: 'hidden' }}>
                  <table className="table table-sm table-hover table-striped small mb-0 table-custom-radius" style={{ tableLayout: 'fixed' }}>
                    <colgroup>
                      <col style={{ width: '60px' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '44%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '10%' }} />
                    </colgroup>
                    <TableHeader columns={[
                      { label: '' },
                      { label: 'DFD', align: 'center' },
                      { label: 'Código', align: 'center' },
                      { label: 'Descrição', align: 'start' },
                      { label: 'Requisitante', align: 'start' },
                      { label: 'Valor Unitário', align: 'right' },
                      { label: 'Quantidade', align: 'right' },
                    ]} />
                  </table>

                  <div style={{ flex: 1, overflowY: 'auto' }}>
                    <table className="table table-sm table-hover table-striped small mb-0 table-custom-radius" style={{ tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: '60px' }} />
                          <col style={{ width: '10%' }} />
                          <col style={{ width: '10%' }} />
                          <col style={{ width: '55%' }} />
                          <col style={{ width: '25%' }} />
                        </colgroup>
                      <tbody>
                        {displayItems && displayItems.length ? (
                          displayItems.map(it => (
                            <tr key={it.id} onClick={() => toggle(it.id, it)} style={{ cursor: 'pointer' }} className={selected[it.id] ? 'table-secondary' : ''}>
                              <td style={{ width: '60px' }}><input type="checkbox" checked={!!selected[it.id]} readOnly /></td>
                              <td style={{ width: '10%' }}>{formatDfd(it.dfdNum)}</td>
                              <td className="text-center" style={{ width: '12%' }}>{it.codigo || it.codigoItem || it.codItem || '-'}</td>
                              <td className="text-start" style={{ width: '44%' }}>{it.descricaoItem}</td>
                              <td className="text-start" style={{ width: '12%' }}>{it.secretariaDepartamento || it.requisitante}</td>
                              <td className="text-end" style={{ width: '10%' }}>{(() => {
                                const v = it.vlrUnitario || it.vlr;
                                if (v === null || v === undefined || v === '') return '-';
                                try { const n = Number(v); return Number.isNaN(n) ? String(v) : new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(n); } catch (e) { return String(v); }
                              })()}</td>
                              <td className="text-end" style={{ width: '10%' }}>{(it.qtdeEstimada || it.qtdeTotal || it.quantidade || it.qtde) ? String(it.qtdeEstimada || it.qtdeTotal || it.quantidade || it.qtde) : '-'}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan="5" className="text-center">Nenhum item encontrado.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="modal-footer py-1">
            <button className="btn btn-secondary btn-sm" onClick={onClose}>Cancelar</button>
            <button className="btn btn-primary btn-sm" onClick={handleConfirm}>Confirmar Consolidação</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PcaConsolidationPickerModal;
